const TONE_CLASSES = {
  blue: {
    card: 'border-blue-200 bg-blue-50 text-blue-900',
    dot: 'border-blue-600 bg-white text-blue-700 ring-blue-200',
    label: 'text-blue-900',
    helper: 'text-blue-700',
    button: 'border-transparent bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600',
  },
  amber: {
    card: 'border-amber-200 bg-amber-50 text-amber-900',
    dot: 'border-amber-500 bg-amber-500 text-white ring-amber-100',
    label: 'text-amber-900',
    helper: 'text-amber-700',
    button: 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 focus-visible:ring-amber-500',
  },
  red: {
    card: 'border-red-200 bg-red-50 text-red-900',
    dot: 'border-red-600 bg-red-600 text-white ring-red-100',
    label: 'text-red-900',
    helper: 'text-red-700',
    button: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 focus-visible:ring-red-600',
  },
  green: {
    card: 'border-green-200 bg-green-50 text-green-900',
    dot: 'border-green-600 bg-green-600 text-white ring-green-100',
    label: 'text-green-900',
    helper: 'text-green-700',
    button: 'border-transparent bg-green-700 text-white hover:bg-green-800 focus-visible:ring-green-700',
  },
  gray: {
    card: 'border-slate-200 bg-slate-50 text-slate-800',
    dot: 'border-slate-300 bg-white text-slate-500 ring-slate-100',
    label: 'text-slate-700',
    helper: 'text-slate-500',
    button: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-500',
  },
  neutral: {
    card: 'border-slate-200 bg-white text-slate-900',
    dot: 'border-slate-300 bg-white text-slate-600 ring-slate-100',
    label: 'text-slate-800',
    helper: 'text-slate-500',
    button: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-500',
  },
};

const STATUS_META = {
  complete: { icon: '✓', iconLabel: 'Complete' },
  current: { icon: '•', iconLabel: 'Current' },
  pending: { icon: '', iconLabel: 'Pending' },
  blocked: { icon: '!', iconLabel: 'Blocked' },
  skipped: { icon: '–', iconLabel: 'Skipped' },
};

const SAFE_ACTION_KINDS = new Set([
  'focus_sms',
  'open_schedule_modal',
  'mark_completed',
  'mark_no_show',
  'retry_failed_text',
  'edit_applicant',
  'view_queue',
  'none',
]);

export function getWorkflowToneClasses(tone) {
  return TONE_CLASSES[tone] || TONE_CLASSES.gray;
}

function buildMeta(helper, timestamp, formatTimestamp) {
  const safeHelper = helper || '';
  const formatted = timestamp && formatTimestamp ? formatTimestamp(timestamp) : '';
  return [safeHelper, formatted].filter(Boolean).join(' · ');
}

export function buildWorkflowRailItems(workflow, { formatTimestamp } = {}) {
  return (workflow?.rail || []).map((step) => {
    const statusMeta = STATUS_META[step.status] || STATUS_META.pending;
    return {
      ...step,
      ...statusMeta,
      meta: buildMeta(step.helper, step.timestamp, formatTimestamp),
      classes: getWorkflowToneClasses(step.tone),
    };
  });
}

function normalizeAction(action, source = 'primary') {
  if (!action || !SAFE_ACTION_KINDS.has(action.kind)) return null;
  return {
    label: action.label || 'Action',
    intent: action.kind,
    disabled: Boolean(action.disabled),
    disabledReason: action.disabledReason || null,
    source,
  };
}

export function getNextActionButtons(nextAction, { contactBlocked = false } = {}) {
  if (!nextAction) return [];
  const actions = [
    normalizeAction(nextAction.primaryAction, 'primary'),
    ...(nextAction.secondaryActions || []).map((action) => normalizeAction(action, 'secondary')),
  ].filter(Boolean);

  return actions
    .filter((action) => !(contactBlocked && action.intent === 'focus_sms'))
    .map((action) => {
      if (contactBlocked && ['focus_sms', 'retry_failed_text'].includes(action.intent)) {
        return {
          ...action,
          disabled: true,
          disabledReason: action.disabledReason || 'Contact is blocked for this applicant',
        };
      }
      return action;
    });
}

export function summarizeCampaign(campaign) {
  return {
    status: campaign?.status || 'not_started',
    label: campaign?.label || 'Campaign not started',
    helper: campaign?.helper || 'Campaign status is current.',
    startedAt: campaign?.startedAt || null,
    lastStepAt: campaign?.lastStepAt || null,
    nextStepAt: campaign?.nextStepAt || null,
    skipReasons: Array.isArray(campaign?.skipReasons) ? campaign.skipReasons.filter(Boolean) : [],
    classes: getWorkflowToneClasses(campaignTone(campaign?.status)),
  };
}

export function campaignTone(status) {
  return {
    active: 'green',
    queued: 'blue',
    paused: 'amber',
    failed: 'red',
    skipped: 'amber',
    completed: 'gray',
    not_started: 'gray',
  }[status] || 'gray';
}
